import React from "react";

import FormField from "./_form-field";
import Results from "./_results";
import phgVars from "./_phg-vars";

class PreliminaryHeightGuide extends React.Component {
  headerStyles = {
    marginBottom: "10px",
    marginRight: "10px",
    display: "inline-block"
  };

  constructor(props) {
    super(props);
    this.state = {
      system: "imperial",
      height: null,
      heightInFeet: null,
      heightInInches: null,
      soil: "28",
      load: "A",
      submitted: false,
      errors: {},
      results: [],
      data: []
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.validateHeight = this.validateHeight.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentDidMount() {
    this.getList();
  }

  handleKeyDown(event) {
    if (event.key === "Enter") {
      this.validateHeight();
    }
  }

  handleChange(event) {
    let id = event.target.name;
    this.setState({ [id]: event.target.value, results: [], submitted: false });
    setTimeout(() => {
      if (id === "system") {
        this.validateHeight();
      }
    });
  }

  async getList() {
    let data = [];
    let productOption = "";

    if (this.props.custom_data?.product) {
      productOption = "/" + this.props.custom_data.product;
    }
    const prefix = process.env.CMS_URL.split(".")[0].replace("https://", "") + productOption;

    await fetch(
      `${process.env.FUNCTIONS_URL}.netlify/functions/get-height-guides?prefix=${encodeURI(prefix)}`
    )
      .then((response) => response.json())
      .then((result) => {
        data = result;
      });

    this.setState({ data });
  }

  getResults() {
    const data = this.state.data.items;
    let results = [];
    const { load, soil, heightInInches, heightInFeet } = this.state;
    const redi_rock_pattern = new RegExp(
      `${load}_${soil}_(B|L|PC|XL|41HC)_[^_]*_${heightInInches}`
    );
    const rosetta_pattern = new RegExp(
      `(NR|XT)_(30E|CB|SH)_${soil}_${load}_${
        heightInFeet.toString().length === 1 ? `0${heightInFeet}` : heightInFeet
      }`
    );

    results = data.filter((f) => {
      if (phgVars.brand === "Redi-Rock") {
        // double checking height so 36 != 360
        const height = f.key.split("_")[4];
        return redi_rock_pattern.test(f.key) && `${height}` === `${heightInInches}`;
      } else {
        return rosetta_pattern.test(f.key);
      }
    });

    setTimeout(() => {
      this.setState({ results: results, submitted: true });
    });
  }

  renderResults() {
    if (this.state.submitted) {
      return this.state.results.length === 0 ? (
        <Results noResults={true} />
      ) : (
        <Results results={this.state.results} info={this.state.data.info} />
      );
    }
  }

  validateHeight() {
    const multiple = phgVars.brand === "Redi-Rock" ? 1.5 : 1;
    let height = this.state.height; // will be new rounded height in feet

    if (this.state.height) {
      const heightInFeet =
        this.state.system == "metric" ? this.state.height * 3.2808 : this.state.height;

      if (heightInFeet < 3 && phgVars.brand === "Redi-Rock") {
        height = 3;
      } else if (heightInFeet < 2 && phgVars.brand === "Rosetta") {
        height = 2;
      } else if (heightInFeet % multiple !== 0) {
        height = Math.round(heightInFeet / multiple) * multiple;
      }

      this.setState({
        heightInInches: height * 12,
        heightInFeet: height,
        height: this.state.system == "imperial" ? height : height / 3.2808
      });
    }
  }

  handleSubmit(event) {
    event.preventDefault();
    this.setState({ submitted: false });
    if (this.state.height) {
      this.setState({ errors: { height: undefined } });
      this.getResults();
    } else {
      this.setState({ errors: { height: "Please enter a valid number." } });
    }
  }

  render() {
    return (
      <div className="container-r-narrow container-lg p-responsive preliminary-height-guide">
        <form onSubmit={this.handleSubmit}>
          {/* -------------------------------------------------- | Step 1 */}
          <FormField
            id="phg1"
            label="1. Select Unit of Measurement"
            legend={`Your choice determines if wall height is measured in feet or meters.`}
          >
            <select
              id="phg1"
              name="system"
              value={this.state["system"]}
              onChange={this.handleChange}
            >
              <option value="imperial">Imperial</option>
              <option value="metric">Metric</option>
            </select>
          </FormField>

          {/* -------------------------------------------------- | Step 2 */}

          <FormField id="phg2" label="2. Define Wall Height" legend={phgVars.legend_2}>
            <input
              id="phg2"
              placeholder={`${phgVars.brand === "Redi-Rock" ? "4.5" : "4"}`}
              name="height"
              value={this.state.height ? this.state.height : ""}
              onChange={this.handleChange}
              onBlur={this.validateHeight}
              onKeyDown={this.handleKeyDown}
            />
            <select
              name="unit"
              disabled
              value={this.state.system == "imperial" ? "feet" : "meters"}
            >
              <option value="feet">Feet</option>
              <option value="meters">Meters</option>
            </select>

            {Object.keys(this.state.errors).includes("height") && (
              <div className="form-error">{this.state.errors["height"]}</div>
            )}
          </FormField>

          {/* -------------------------------------------------- | Step 3 */}

          <FormField
            id="phg3"
            label="3. Define Soil Type"
            legend={`Select between four general soil types. General descriptions and an assumed internal
            angle of friction are provided for different soils. Care must be taken in this step.
            Soil determination should be performed by a person trained to evaluate soils as the
            selection of the internal soil strength has an important impact on wall analysis
            calculations.`}
          >
            <select id="phg3" name="soil" value={this.state["soil"]} onChange={this.handleChange}>
              <option value="28">Φ = 28° - Silty Sand, Clayey Sand</option>
              <option value="30">Φ = 30° - Silty Sand, Fine to Medium Sand</option>
              <option value="34">Φ = 34° - Dense Well Graded Sand, Sand and Gravel</option>
              {phgVars.brand === "Redi-Rock" && (
                <option value="4026">
                  Φ = 40° over 26°- Crushed Stone Backfill Replacing Silty or Clayey Sand
                </option>
              )}
              {phgVars.brand === "Rosetta" && (
                <option value="4028">
                  Φ = 40° over 28°- Crushed Stone Backfill Replacing Silty or Clayey Sand
                </option>
              )}
            </select>
          </FormField>

          {/* -------------------------------------------------- | Step 4 */}

          <FormField
            fullWidth={true}
            id="phg4"
            label="4. Define Load Conditions"
            legend={`Select flat areas, traffic loads, or slopes on the top of the wall. Note that
            sections with a slope at the bottom, or toe, of the wall are not shown here. A toe
            slope condition needs to be carefully analyzed based on detailed site conditions.`}
          >
            <div className="load-conditions radio-group">
              <label>
                {(() => {
                  switch (this.props.custom_data?.product) {
                    case "Grand Ledge":
                      return (
                        <img src="/images/GL_no-shurcharge_no-back-slope_no-toe-slope.png" alt="" />
                      );
                    case "Outcropping":
                    default:
                      return <img src="/images/LoadConditionA.png" alt="" />;
                  }
                })()}

                <span>
                  {phgVars.brand === "Redi-Rock" && <span>No Slope, No Surcharge</span>}

                  {phgVars.brand === "Rosetta" && (
                    <span>No Surcharge, No Back Slope, No Toe Slope</span>
                  )}

                  <input
                    type="radio"
                    name="load"
                    value="A"
                    checked={this.state["load"] == "A"}
                    onChange={this.handleChange}
                  />
                </span>
              </label>
              {phgVars.brand === "Redi-Rock" && (
                <label>
                  <img src="/images/LoadConditionB.png" alt="" />
                  <span>
                    <span>250psf (12 kPa) Live Load Surcharge</span>

                    <input
                      type="radio"
                      name="load"
                      value="B"
                      checked={this.state["load"] == "B"}
                      onChange={this.handleChange}
                    />
                  </span>
                </label>
              )}

              {phgVars.brand === "Rosetta" && (
                <label>
                  {(() => {
                    switch (this.props.custom_data?.product) {
                      case "Grand Ledge":
                        return <img src="/images/gl_100-and-250-psf-surcharge.png" alt="" />;
                      case "Outcropping":
                      default:
                        return <img src="/images/LoadConditionB.png" alt="" />;
                    }
                  })()}
                  <span>
                    <span>100psf (4.8 kPa) Surcharge, No Backslope, No Toe Slope</span>

                    <input
                      type="radio"
                      name="load"
                      value="B100"
                      checked={this.state["load"] == "B100"}
                      onChange={this.handleChange}
                    />
                  </span>
                </label>
              )}

              {phgVars.brand === "Rosetta" && (
                <label>
                  {(() => {
                    switch (this.props.custom_data?.product) {
                      case "Grand Ledge":
                        return <img src="/images/gl_100-and-250-psf-surcharge.png" alt="" />;
                      case "Outcropping":
                      default:
                        return <img src="/images/LoadConditionB.png" alt="" />;
                    }
                  })()}
                  <span>
                    <span>250psf (12 kPa) Surcharge, No Backslope, No Toe Slope</span>

                    <input
                      type="radio"
                      name="load"
                      value="B250"
                      checked={this.state["load"] == "B250"}
                      onChange={this.handleChange}
                    />
                  </span>
                </label>
              )}

              <label>
                {(() => {
                  switch (this.props.custom_data?.product) {
                    case "Grand Ledge":
                      return <img src="/images/gl_no-surcharge_1-25-backslope.png" alt="" />;
                    case "Outcropping":
                    default:
                      return <img src="/images/LoadConditionC.png" alt="" />;
                  }
                })()}
                <span>
                  {phgVars.brand === "Redi-Rock" && <span>1:2.5 Slope</span>}

                  {phgVars.brand === "Rosetta" && (
                    <span>No Surcharge, 1:2.5 (21.8°) Backslope, No Toe Slope</span>
                  )}
                  <input
                    type="radio"
                    name="load"
                    value="C"
                    checked={this.state["load"] == "C"}
                    onChange={this.handleChange}
                  />
                </span>
              </label>
            </div>
          </FormField>

          {/* -------------------------------------------------- | Submit */}

          <button className="btn" type="submit">
            Search for Wall Sections
          </button>
        </form>

        {/* -------------------------------------------------- | Results */}

        <div className="results">
          <hr />
          {this.renderResults()}
          <p>
            <strong>Still not finding what you are looking for?</strong> Call a {phgVars.brand}{" "}
            engineer at <a href={`tel:1-${phgVars.phone}`}>{phgVars.phone}</a>.
          </p>
        </div>
      </div>
    );
  }
}

export default PreliminaryHeightGuide;
